/**
 * Spec 217 PR4.b — `/broadcasts` route (read-only placeholder).
 *
 * Spec 217 §4.4 marks broadcasts as "feature genuinamente nueva, no
 * port" and explicitly defers the wire format until Team AFM / Ruben
 * validate the use case. Until that decision lands we ship a routed
 * placeholder so:
 *   1. The sidebar has a destination to link to (no dead link).
 *   2. The role gate (admin/creator only) can be exercised by E2E
 *      tests today, which keeps the contract locked when the real
 *      feature lands.
 *   3. The `/broadcasts` URL becomes deep-linkable so a future push
 *      notification can route directly here.
 *
 * The empty state uses the same illustrated pattern as `/works`'s
 * empty state so a member who navigates here (admin only by gate,
 * but visiting via direct URL) sees a consistent surface.
 *
 * When the real feature ships, replace the placeholder with the
 * list + filter + detail flow. The route + role gate stay; the body
 * is what changes.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as ToggleGroup from '@radix-ui/react-toggle-group';
import { Megaphone } from 'lucide-react';

import { EmptyState } from '../components/EmptyState';
import type { WebIdentity } from '../store/keys';

import './Broadcasts.css';

type StatusFilter = 'all' | 'unread' | 'read';

const STATUS_OPTIONS: ReadonlyArray<{ key: StatusFilter; label: string }> = [
  { key: 'all', label: 'Todos' },
  { key: 'unread', label: 'Sin leer' },
  { key: 'read', label: 'Leídos' },
];

interface BroadcastsProps {
  identity: WebIdentity;
}

export function Broadcasts({ identity: _identity }: BroadcastsProps) {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<StatusFilter>('all');

  // Real broadcasts come from a relay-backed store the spec hasn't
  // landed yet. The placeholder list is empty by design — the toggle
  // group is wired so the contract is exercisable from E2E even
  // before the data feed exists.
  const broadcasts: ReadonlyArray<{ id: string; title: string; status: 'unread' | 'read' }> = [];
  const visible = filter === 'all' ? broadcasts : broadcasts.filter((b) => b.status === filter);

  return (
    <div className="bc-shell" data-testid="broadcasts-screen">
      <header className="bc-header">
        <div className="bc-header-inner">
          <Megaphone size={24} strokeWidth={2} aria-hidden="true" />
          <span className="bc-brand">Avisos del equipo</span>
        </div>
      </header>

      <main id="main-content" className="bc-main" tabIndex={-1}>
        {/* sr-only h1 — paired layout hides .bc-header (same rule
            as Settings/Stats/WorkList in AppShell.css). */}
        <h1 className="sr-only">Avisos del equipo</h1>

        {broadcasts.length > 0 && (
          <ToggleGroup.Root
            className="bc-filters"
            type="single"
            value={filter}
            onValueChange={(v) => {
              if (v) setFilter(v as StatusFilter);
            }}
            aria-label="Filtrar avisos por estado"
            data-testid="broadcasts-filters"
          >
            {STATUS_OPTIONS.map((opt) => (
              <ToggleGroup.Item
                key={opt.key}
                value={opt.key}
                className="bc-chip"
                data-testid={`broadcasts-filter-${opt.key}`}
              >
                {opt.label}
              </ToggleGroup.Item>
            ))}
          </ToggleGroup.Root>
        )}

        {visible.length === 0 ? (
          <EmptyState
            variant="noResults"
            title="Avisos de equipo en camino"
            body="Pronto podrás enviar avisos a tu equipo desde aquí. Mientras tanto, usa los comentarios en cada trabajo para coordinar."
            action={{
              label: 'Ver trabajos',
              onClick: () => navigate('/'),
              testId: 'broadcasts-back',
            }}
            testId="broadcasts-empty"
          />
        ) : (
          <ul className="bc-list" data-testid="broadcasts-list">
            {visible.map((b) => (
              <li key={b.id} data-testid={`broadcasts-row-${b.id}`}>
                {b.title}
              </li>
            ))}
          </ul>
        )}
      </main>
    </div>
  );
}
